import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language].footer;
  const tNav = translations[language].nav;

  // Reconstruct categories from translations to ensure they're dynamic
  const modelsCategories = [
    {
      key: 'offRoad',
      label: tNav.offRoad,
      subItems: [
        { name: 'BJ30 Hybrid', href: '/off-road/bj30-hybrid' },
        { name: 'BJ40 HONOR', href: '/off-road/bj40-honor' },
        { name: 'BJ40 PRO', href: '/off-road/bj40-pro' },
      ]
    },
    {
      key: 'sedan',
      label: tNav.sedan,
      subItems: [
        { name: 'U5 PLUS', href: '/sedan/u5-plus' }
      ]
    }
  ];

  const quickLinksRef = [
    { name: t.links.aboutBaic, href: '/about/overview' },
    { name: t.links.ggs, href: '/about/ggs' },
    { name: t.links.vision, href: '/about/vision' },
    { name: t.links.afterSales, href: '/about/after-sales-service' },
    { name: t.links.careers, href: '/connect/careers' },
    { name: t.links.contact, href: '/connect/contact-us' },
    { name: t.links.privacyPolicy, href: '/privacy-policy' },
    { name: t.links.termsConditions, href: '/terms-and-conditions' },
  ];

  const servicesLinksRef = [
    { name: t.links.showroom, href: '/show-rooms' },
    { name: t.links.serviceCenter, href: '/service-centers' },
    { name: tNav.testDrive, href: '/test-drive' },
    { name: t.links.bookService, href: '/book-service' },
    { name: t.links.warranty, href: '/car-service' },
  ];

  const historyLinksRef = [
    { name: tNav.baicHistory, href: '/history/baic' },
    { name: tNav.offRoadHistory, href: '/history/off-road' },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex flex-col items-start gap-4 mb-6">
              <img src="/assets/baic-logo.webp" alt="BAIC" className="h-8 ps-6 w-auto object-contain" />
              <div className="flex flex-col gap-1">
                <span className="text-sm ps-6 text-gray-400 font-light tracking-wider">{t.companyInfo}</span>
                <img src="/assets/ggsllc-white.png" alt="Gulf Great Sands LLC" className="h-14 w-auto object-contain" />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.quickLinks}</h4>
            <div className="space-y-2">
              <Accordion type="single" collapsible className="w-1/2 border-none">
                {/* Models Accordion */}
                <AccordionItem value="models" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline hover:text-red-600 text-gray-400 font-normal">
                    {t.links.models}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col space-y-4 pl-4 pt-2">
                      {modelsCategories.map((category) => (
                        <div key={category.key}>
                          <h5 className="text-gray-500 font-semibold mb-2 text-xs uppercase tracking-wider">{category.label}</h5>
                          <ul className="space-y-2 border-l border-gray-800 pl-3">
                            {category.subItems.map((model) => (
                              <li key={model.name}>
                                <Link
                                  to={model.href}
                                  className="text-gray-400 hover:text-red-600 transition-colors text-sm block"
                                >
                                  {model.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* History Accordion */}
                <AccordionItem value="history" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline hover:text-red-600 text-gray-400 font-normal">
                    {t.links.history}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pl-4 pt-2 border-l border-gray-800 ml-1">
                      {historyLinksRef.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className="text-gray-400 hover:text-red-600 transition-colors text-sm block"
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Standard Links */}
              <ul className="space-y-3 mt-1">
                {quickLinksRef.map((link, idx) => (
                  <li key={`${link.name}-${idx}`}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-red-600 transition-colors block py-0.5"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.services}</h4>
            <ul className="space-y-3">
              {servicesLinksRef.map((item, idx) => (
                <li key={`${item.name}-${idx}`}>
                  <Link
                    to={item.href}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.contactUs}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-red-600 flex-shrink-0 mt-1" />
                <div className="text-gray-400">{t.contact.call}: 80050800</div>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-1" />
                <div className="text-gray-400">{t.contact.whatsapp}: +96879005800</div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-red-600 flex-shrink-0 mt-1" />
                <a
                  href="mailto:info@baicoman.com"
                  className="text-gray-400 hover:text-red-600 transition-colors"
                >
                  info@baicoman.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-red-600 flex-shrink-0 mt-1" />
                <div className="text-gray-400">
                  {t.contact.address}
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
          <p className="text-xs text-gray-500 mb-4 max-w-4xl mx-auto px-4">
            {t.disclaimer}
          </p>
          <p>
            &copy; {new Date().getFullYear()} GGS. {t.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;